<template>
  <div
    class="shadow mt-8 border overflow-hidden rounded-lg"
    :class="{ 'opacity-50': canBeContinued }"
  >
    <!-- Header -->
    <div class="p-4 pb-5 sm:px-6 border-b bg-gray-50">
      <div class="flex flex-col md:flex-row justify-between gap-3">
        <!-- Title and info -->
        <div class="flex items-start gap-1 flex-col">
          <h3
            class="text-xl leading-0 font-medium text-vice-teal-900"
            :class="{ 'cursor-pointer hover:text-vice-teal-700': !isDraft }"
            @click="openSessionDetails"
          >
            {{ sessionDetails.title }}
          </h3>
          <div class="flex gap-5 mt-0.5">
            <p class="flex gap-1">
              <CalendarIcon class="h-5 w-5 text-gray-300"></CalendarIcon>
              <span class="text-gray-900 text-xs">
                {{ yearFormatter(sessionDetails.details.session_start_by_timezone) }}
              </span>
            </p>
            <p class="flex gap-1">
              <ClockIcon class="h-5 w-5 text-gray-300"></ClockIcon>
              <span class="text-gray-900 text-xs">
                {{ hoursFormatter(sessionDetails.details.session_start_by_timezone) }}
              </span>
            </p>
          </div>

          <div v-if="!isPastSession" class="flex gap-1">
            <Transition>
              <ViceBadge red small v-if="!!sessionStartsIn">
                {{ sessionStartsIn }}
              </ViceBadge>
            </Transition>
            <ViceBadge v-if="isDraft" yellow small>Draft</ViceBadge>
          </div>
        </div>
        <!-- Actions -->
        <div class="flex items-end md:flex-col gap-2 flex-none">
          <template v-if="isPastSession">
            <ViceButton
              data-cy="delete-session"
              v-if="!isDraft && !canBeContinued"
              red
              small
              @click="deleteSession"
              class="sm:w-40 w-44 justify-center"
              icon="TrashIcon"
            >
              Delete session
            </ViceButton>
            <ViceButton
              data-cy="start-session"
              v-if="!isDraft && canBeContinued"
              icon="LogoutIcon"
              :loading="false"
              red
              @click="
                continueSession = 1;
                openModal();
              "
            >
              Continue session
            </ViceButton>
          </template>
          <template v-else>
            <ViceButton
              data-cy="start-session"
              v-if="!isDraft && !$page.props.isAdmin && !isMobile"
              icon="LogoutIcon"
              red
              :loading="false"
              @click="openModal()"
              class="step-start-session"
            >
              Start session
            </ViceButton>

            <inertia-link :href="route('edit.session', sessionDetails.unique_id)">
              <ViceButton
                data-cy="edit-session"
                icon="PencilIcon"
                small
                :loading="false"
                class="step-edit-session"
              >
                Edit session
              </ViceButton>
            </inertia-link>
          </template>
        </div>
      </div>
    </div>

    <!-- Body -->
    <div class="px-4 py-4 bg-white sm:px-6 flex flex-grow" v-if="sessionDetails.details.clinician">
      <div class="flex-shrink-0 group block">
        <div class="flex items-center">
          <div>
            <ViceAvatar
              :image="sessionDetails.details.clinician?.profile_photo_url"
              :name="sessionDetails.details.clinician?.name"
              class="w-8 h-8"
            />
          </div>
          <div class="ml-3">
            <p class="text-sm font-medium text-gray-700 group-hover:text-gray-900">
              {{
                (sessionDetails.details.clinician?.details?.title
                  ? sessionDetails.details.clinician?.details?.title + ' '
                  : '') + sessionDetails.details.clinician?.name
              }}

              <span v-if="sessionDetails.details.clinician?.deleted_at">(archived)</span>
            </p>
            <p
              v-if="sessionDetails.details.clinician.details"
              class="text-xs font-medium text-gray-500 group-hover:text-gray-700"
            >
              {{ sessionDetails.details.clinician.details.position }}
            </p>
          </div>
        </div>
      </div>
      <div class="flex flex-grow items-center justify-end">
        <GroupOfPeopleIcon v-if="!sessionDetails.is_private" />
        <LockIcon v-if="sessionDetails.is_private" />
        <p class="ml-1.75 text-sm font-medium text-vice-teal-900 group-hover:text-gray-800">
          {{ sessionDetails.is_private ? 'Private' : 'Public' }}
        </p>
      </div>
    </div>
  </div>

  <TransitionRoot as="template" :show="open">
    <Dialog as="div" class="fixed z-10 zoom-80 inset-0 overflow-y-auto" @close="closeModal()">
      <div
        class="
          flex
          items-end
          justify-center
          min-h-screen
          pt-4
          px-4
          pb-20
          text-center
          sm:block
          sm:p-0
        "
      >
        <TransitionChild
          as="template"
          enter="ease-out duration-300"
          enter-from="opacity-0"
          enter-to="opacity-100"
          leave="ease-in duration-200"
          leave-from="opacity-100"
          leave-to="opacity-0"
        >
          <DialogOverlay class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </TransitionChild>

        <!-- This element is to trick the browser into centering the modal contents. -->
        <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
          &#8203;
        </span>
        <TransitionChild
          as="template"
          enter="ease-out duration-300"
          enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          enter-to="opacity-100 translate-y-0 sm:scale-100"
          leave="ease-in duration-200"
          leave-from="opacity-100 translate-y-0 sm:scale-100"
          leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
        >
          <div
            class="
              inline-block
              align-bottom
              bg-white
              rounded-lg
              px-4
              pt-5
              pb-4
              text-left
              overflow-hidden
              shadow-xl
              transform
              transition-all
              sm:my-8
              sm:align-middle
              sm:max-w-5xl
              sm:w-full
              sm:p-12
            "
          >
            <div>
              <div class="ml-0">
                <h2 class="text-xl leading-6 font-bold text-vice-teal-900">
                  Before you start the session
                </h2>
                <p class="font-medium text-md via-vice-dark-teal mt-3">
                  Please inform the patient that:
                </p>
              </div>
              <ul
                role="list"
                class="
                  divide-y divide-gray-300
                  bg-vice-light-grey
                  py-5
                  px-12
                  rounded-lg
                  my-7
                  step-session-info
                "
              >
                <li class="py-4 flex items-center">
                  <img class="h-10 w-12" src="/image/icons/education-icon.svg" />
                  <div class="ml-3">
                    <p class="text-sm text-gray-500">
                      The consultation will be transmitted to students for
                      <span class="font-medium text-gray-900">educational purposes.</span>
                    </p>
                  </div>
                </li>
                <li v-if="hasRecordingAndSnapshotFeatures" class="py-4 flex items-center">
                  <img class="h-10 w-16" src="/image/icons/gdpr-icon.svg" />
                  <div class="ml-3">
                    <p class="text-sm text-gray-500">
                      Any recordings will be carried out according to guidelines issued by the
                      General Medical Council, and will be stored securely in line with the
                      <span class="font-medium text-gray-900">educational purposes.</span>
                    </p>
                  </div>
                </li>
                <li class="py-4 flex items-center">
                  <img class="h-10 w-12" src="/image/icons/eye-icon.svg" />
                  <div class="ml-3">
                    <p class="text-sm text-gray-500">
                      The transmission will be
                      <span class="font-medium text-gray-900">
                        stopped during any intimate examination
                      </span>
                      , should this be required during the consultation.
                    </p>
                  </div>
                </li>
                <li class="py-4 flex items-center">
                  <img class="h-10 w-12 items-center" src="/image/icons/stop-hand-icon.svg" />
                  <div class="ml-3">
                    <p class="text-sm text-gray-500">
                      The patient can
                      <span class="font-medium text-gray-900">
                        stop the transmission at any time
                      </span>
                      . This will not affect the consultation with the clinician.
                    </p>
                  </div>
                </li>
              </ul>
            </div>
            <div class="flex justify-center gap-2 items-center">
              <input
                data-cy="agree-terms-clinician-session"
                id="agree-checkbox"
                type="checkbox"
                v-model="checkbox"
                class="
                  step-confirm-checkbox
                  focus:ring-vice-teal-500
                  h-4
                  w-4
                  text-vice-teal-600
                  border-gray-300
                  rounded
                  cursor-pointer
                "
              />
              <label
                for="agree-checkbox"
                class="cursor-pointer text-md text-vice-dark-grey font-bold"
              >
                I have read the
                <a :href="route('dashboard.clinician', { tab: 'terms-of-use' })" target="_blank">
                  <span class="text-vice-teal-400 underline">Terms of Use</span>
                </a>
                and am aware of my responsibilities and will act upon them.
              </label>
            </div>
            <div class="mt-5 sm:mt-6 flex justify-around">
              <ViceButton
                data-cy="entry-start-session"
                @click="startSession"
                class="cursor-pointer step-confirm-start-session"
                :class="[
                  {
                    'sm:bg-gray-150 sm:text-black opacity-60':
                      !checkbox || presenceChecking || alreadyPresent,
                  },
                  { 'cursor-not-allowed': alreadyPresent },
                ]"
                icon="LogoutIcon"
                red
                :loading="false"
              >
                Start session
              </ViceButton>
            </div>
            <div
              v-if="alreadyPresent"
              class="
                flex
                gap-2
                justify-center
                items-center
                mt-5
                p-5
                rounded-md
                border-2
                bg-vice-rose-400
                transition-all
              "
              :class="[{ ' highlighted ': highlighted }]"
            >
              <p class="text-xs font-medium">
                You already have a VCE session open in another tab or window. Please close the
                existing session before starting a new one.
              </p>
            </div>
          </div>
        </TransitionChild>
      </div>
    </Dialog>
  </TransitionRoot>

  <SessionEntryPrecheckWizard
    :devices="devices"
    :session="sessionDetails"
    :precheckOpen="isOpenSessionEntryPrecheck"
    @openSessionEntryWizard="openPoliciesModal"
    @closeSessionEntryPrecheckWizard="isOpenSessionEntryPrecheck = false"
  />
</template>

<script>
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import relativeTime from 'dayjs/plugin/relativeTime';

import { CalendarIcon, ClockIcon } from '@heroicons/vue/solid';
import { Dialog, DialogOverlay, TransitionChild, TransitionRoot } from '@headlessui/vue';
import { Inertia } from '@inertiajs/inertia';
import SessionEntryPrecheckWizard from '@/Pages/User/Details/SessionEntryPrecheckWizard.vue';
import { SESSION_STATUS } from '@/Utils/vice-constants';
import { isMobileDevice } from '@/Utils/helpers';
import DateFormatMixin from '@/Mixins/DateFormatMixin.vue';
import ViceBadge from '@/Components/Badges/ViceBadge.vue';
import ViceButton from '@/Components/Buttons/ViceButton.vue';
import ViceAvatar from '@/Components/Avatars/ViceAvatar.vue';
import useTourManager from '@/Composables/useTourManager';
import { clinicianStartFirstSessionSteps, clinicianEntryModalTourSteps } from '@/Data/tourSteps';
import GroupOfPeopleIcon from '@/Components/Icons/GroupOfPeopleIcon.vue';
import LockIcon from '@/Components/Icons/LockIcon.vue';
import SessionAnalyticsService from '@/Services/SessionAnalyticsService';

const clinicianStartFirstSessionTour = useTourManager(
  'clinicianStartFirstSessionTour',
  clinicianStartFirstSessionSteps,
);
const clinicianEntryModalTour = useTourManager(
  'clinicianEntryModalTour',
  clinicianEntryModalTourSteps,
);

dayjs.extend(duration);
dayjs.extend(relativeTime);

export default {
  name: 'CardClinicianSession',
  emits: ['deleteSession'],
  props: {
    sessionDetails: {
      required: true,
    },
    isPastSession: {
      default: false,
    },
  },
  mixins: [DateFormatMixin],
  components: {
    LockIcon,
    GroupOfPeopleIcon,
    ViceBadge,
    ViceButton,
    CalendarIcon,
    ClockIcon,
    ViceAvatar,
    Dialog,
    DialogOverlay,
    TransitionChild,
    TransitionRoot,
    SessionEntryPrecheckWizard,
  },
  setup(props, { emit }) {
    const deleteSession = () => {
      emit('deleteSession');
    };

    const devices = {
      video: props.sessionDetails.clinician_devices,
      audio: props.sessionDetails.clinician_default_devices,
    };
    return {
      devices,
      deleteSession,
      SESSION_STATUS,
    };
  },
  mounted() {
    setTimeout(() => {
      if (!this.isPastSession) {
        clinicianStartFirstSessionTour.startTour();
      }
    }, 1000);
    this.sessionStartsIn = this.updateSessionStartTime();
    setInterval(() => {
      this.sessionStartsIn = this.updateSessionStartTime();
    }, 10000);
  },
  data() {
    return {
      sessionStartsIn: null,
      open: false,
      isOpenSessionEntryPrecheck: false,
      continueSession: 0,
      checkbox: false,
      alreadyPresent: false,
      presenceChecking: true,
      presenceInterval: null,
      highlighted: false,
      isMobile: isMobileDevice(),
    };
  },
  methods: {
    openSessionDetails() {
      if (this.isDraft) return;
      Inertia.visit(route('session.clinician.details', this.sessionDetails.unique_id));
    },
    startSession() {
      if (this.checkbox && !this.alreadyPresent) {
        this.closeModal();
        localStorage.setItem('_vice:clinicianEntryModalTour_current_step', 3);
        window.open(
          route('session.clinician.show', {
            unique_id: this.sessionDetails.unique_id,
            continue: this.continueSession,
          }),
          '_blank',
        );
      }

      if (this.alreadyPresent) {
        this.highlighted = true;
        setTimeout(() => {
          this.highlighted = false;
        }, 600);
      }
    },
    updateSessionStartTime() {
      const now = dayjs();
      const start = dayjs(this.sessionDetails.details.session_start_by_timezone);
      const end = dayjs(this.sessionDetails.details.session_end_by_timezone);
      const durationMoment = dayjs.duration(start.diff(now));
      const seconds = durationMoment.asSeconds();

      if (now.isAfter(end)) return 'Finished';
      if (seconds <= 0) return 'Has started!';
      if (seconds > 3600 * 24) return null;

      return `Starts in ${dayjs.duration(seconds, 'seconds').humanize()}`;
    },

    async openModal() {
      this.isOpenSessionEntryPrecheck = true;
    },

    openPoliciesModal() {
      this.open = true;
      // Check if user is already present in the session
      this.presenceInterval = setInterval(() => {
        this.checkUserPresence();
      }, 1000);

      setTimeout(() => {
        clinicianEntryModalTour.startTour();
      }, 200);
    },

    async checkUserPresence() {
      try {
        this.alreadyPresent = await SessionAnalyticsService.isUserAlreadyPresent(
          this.sessionDetails.unique_id,
          this.$page.props.user.unique_id,
        ).then((isPresented) => {
          this.presenceChecking = false;
          return isPresented;
        });
      } catch (error) {
        console.error('Error checking user presence:', error);
      }
    },

    closeModal() {
      if (this.presenceInterval) {
        clearInterval(this.presenceInterval);
        this.presenceInterval = null;
      }

      clinicianEntryModalTour.cancelTour();
      this.open = false;
      this.checkbox = false;
    },
  },
  computed: {
    isDraft() {
      return this.sessionDetails.status === SESSION_STATUS.DRAFT;
    },
    canBeContinued() {
      const now = dayjs();
      const sessionEnd = dayjs(this.sessionDetails.details.session_end_by_timezone);

      return now.isSame(sessionEnd, 'day') && now.isAfter(sessionEnd);
    },
    hasRecordingAndSnapshotFeatures() {
      return (
        this.sessionDetails?.details?.university?.snapshot_feature &&
        this.sessionDetails?.details?.university?.video_snapshot_feature
      );
    },
  },
};
</script>

<style scoped>
.v-enter-active,
.v-leave-active {
  transition: opacity 0.5s ease;
}

.v-enter-from,
.v-leave-to {
  opacity: 0;
}

.highlighted {
  background-color: rgb(255 84 109);
  border: 2px solid #bd0517;
  box-shadow: 0 0 10px rgb(69, 0, 14);
  transition: all 0.3s ease;
}
</style>
